// Share dialog (DS ShareDialog, SD35): DS Dialog titled "Share", the link row (LinkField with
// its purple Copy button), "Who can watch" as the VisibilityPicker, Hind Bold section labels
// and a secondary Done. What the API serves is unchanged: visibility
// (§4 #21 PATCH), grants (#23-25) and the public link (#26-27). F3/FR-SH5a: the link protections
// (password, expiry, view limit, email gate, allowed domains) are live on a public link via
// PATCH /grackles/{id}/link, and the section is shown only while there is one.
//
// `published` is the post-stop state: ProcessingPage opens it the moment the recording is ready
// to watch, titled "Your link is ready".
import { useEffect, useState } from "react";
import { Button, Callout, Dialog, Icon, Input, LinkField, Switch } from "@grackle/ui";
import { api, ApiFail } from "../api/client";
import { formatDuration } from "../library/format";
import BandwidthLimitRow from "./BandwidthLimitRow";
import {
  clearExpiryBody,
  clearMaxViewsBody,
  clearPasswordBody,
  saveDomainsBody,
  setExpiryBody,
  setMaxViewsBody,
  setPasswordBody,
  toLocalInputValue,
  toggleRequireEmailBody,
  type PatchLinkRequest,
} from "./shareProtections";
import { FOOTNOTE, useShareSettings, WhoCanWatchSection } from "./WhoCanWatch";
import "./watch.css";

interface CreateLinkResponse {
  token: string;
  url: string;
}

/** F3 (link protections): the five DS rows, shown only with a live public link. */
export const PROTECTIONS = [
  { icon: "password", label: "Password" },
  { icon: "calendar-x", label: "Expires" },
  { icon: "eye", label: "View limit" },
  { icon: "at", label: "Email gate" },
  { icon: "globe-simple", label: "Domain only" },
] as const;

/** "https://grackle.app/s/abc" -> "grackle.app/s/abc" for the mono field; the copy is exact. */
function displayUrl(url: string): string {
  return url.replace(/^https?:\/\//, "");
}

export default function ShareDialog({
  grackleId,
  onClose,
  published = false,
}: {
  grackleId: string;
  onClose: () => void;
  /** DS 1d's post-stop state: "Your link is ready" */
  published?: boolean;
}) {
  const settings = useShareSettings(grackleId);
  const { detail, state, busy, error, loadShare, run } = settings;

  // F3/FR-SH5a: the protection-row form fields, synced from the link's live protections
  // whenever the link's token changes (first load, or a brand-new link after revoke+create)
  // — not on every `loadShare()`, so a save in flight never clobbers what the user is typing
  // in an unrelated field.
  const [password, setPassword] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [maxViews, setMaxViews] = useState("");
  const [requireEmail, setRequireEmail] = useState(false);
  const [domainsInput, setDomainsInput] = useState("");
  const [protectBusy, setProtectBusy] = useState(false);
  const [protectError, setProtectError] = useState<string | null>(null);

  useEffect(() => {
    const link = state?.link;
    if (!link) return;
    setPassword("");
    setExpiresAt(link.protections.expires_at ? toLocalInputValue(link.protections.expires_at) : "");
    setMaxViews(link.protections.max_views != null ? String(link.protections.max_views) : "");
    setRequireEmail(link.protections.require_email);
    setDomainsInput((link.protections.allowed_email_domains ?? []).join(", "));
    setProtectError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.link?.token]);

  useEffect(() => {
    function onKeydown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeydown);
    return () => document.removeEventListener("keydown", onKeydown);
  }, [onClose]);

  function createLink() {
    void run(async () => {
      await api<CreateLinkResponse>("POST", `/grackles/${grackleId}/link`);
      await loadShare();
    }, "Couldn't create the link.");
  }

  function revokeLink() {
    void run(async () => {
      await api<void>("DELETE", `/grackles/${grackleId}/link`);
      await loadShare();
    }, "Couldn't revoke the link.");
  }

  async function patchProtections(body: PatchLinkRequest, onFail: string) {
    if (protectBusy) return;
    setProtectBusy(true);
    setProtectError(null);
    try {
      await api("PATCH", `/grackles/${grackleId}/link`, body);
      await loadShare();
    } catch (err) {
      setProtectError(err instanceof ApiFail ? err.message : onFail);
    } finally {
      setProtectBusy(false);
    }
  }

  async function savePassword() {
    if (!password) return;
    await patchProtections(setPasswordBody(password), "Couldn't set the password.");
    setPassword("");
  }

  async function clearPassword() {
    await patchProtections(clearPasswordBody(), "Couldn't remove the password.");
  }

  async function saveExpiry() {
    if (!expiresAt) return;
    await patchProtections(setExpiryBody(expiresAt), "Couldn't update the expiry.");
  }

  async function clearExpiry() {
    setExpiresAt("");
    await patchProtections(clearExpiryBody(), "Couldn't clear the expiry.");
  }

  async function saveMaxViews() {
    const result = setMaxViewsBody(maxViews);
    if ("error" in result) {
      setProtectError(result.error);
      return;
    }
    await patchProtections(result, "Couldn't update the view limit.");
  }

  async function clearMaxViews() {
    setMaxViews("");
    await patchProtections(clearMaxViewsBody(), "Couldn't clear the view limit.");
  }

  async function toggleRequireEmail(next: boolean) {
    setRequireEmail(next);
    await patchProtections(toggleRequireEmailBody(next, domainsInput), "Couldn't update the email gate.");
  }

  async function saveDomains() {
    if (!requireEmail) return;
    await patchProtections(saveDomainsBody(domainsInput), "Couldn't update the allowed domains.");
  }

  const visibility = state?.visibility;
  // Every level but public shares the watch URL itself: the viewer still has to be allowed in.
  // Public shares its token link (§4 #26), which works without signing in.
  const watchUrl = `${window.location.origin}/g/${grackleId}`;
  const linkUrl = visibility === "public" ? state?.link?.url ?? null : watchUrl;

  // F3: protections only apply to a live public link.
  const protections = visibility === "public" && state?.link ? state.link.protections : null;

  function protectionControls(label: (typeof PROTECTIONS)[number]["label"]) {
    if (!protections) return null;
    switch (label) {
      case "Password":
        return protections.has_password ? (
          <div className="share__protection-controls">
            <span className="gk-caption">Set — never shown again</span>
            <Button variant="secondary" size="sm" onClick={() => void clearPassword()} disabled={protectBusy}>
              Remove
            </Button>
          </div>
        ) : (
          <div className="share__protection-controls">
            <Input
              type="password"
              placeholder="Set a password"
              aria-label="Link password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={protectBusy}
              className="share__protection-field"
            />
            <Button variant="secondary" size="sm" onClick={() => void savePassword()} disabled={protectBusy || !password}>
              Set
            </Button>
          </div>
        );
      case "Expires":
        return (
          <div className="share__protection-controls">
            <Input
              type="datetime-local"
              aria-label="Link expiry"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              disabled={protectBusy}
              className="share__protection-field"
            />
            <Button variant="secondary" size="sm" onClick={() => void saveExpiry()} disabled={protectBusy || !expiresAt}>
              Save
            </Button>
            {protections.expires_at ? (
              <Button variant="ghost" size="sm" onClick={() => void clearExpiry()} disabled={protectBusy}>
                Clear
              </Button>
            ) : null}
          </div>
        );
      case "View limit":
        return (
          <div className="share__protection-controls">
            <Input
              type="number"
              min={1}
              placeholder="No limit"
              aria-label="Max views"
              value={maxViews}
              onChange={(e) => setMaxViews(e.target.value)}
              disabled={protectBusy}
              wrapperStyle={{ width: 120 }}
            />
            <Button variant="secondary" size="sm" onClick={() => void saveMaxViews()} disabled={protectBusy || !maxViews}>
              Save
            </Button>
            {protections.max_views != null ? (
              <>
                <span className="gk-caption">{protections.view_count} viewed so far</span>
                <Button variant="ghost" size="sm" onClick={() => void clearMaxViews()} disabled={protectBusy}>
                  Clear
                </Button>
              </>
            ) : null}
          </div>
        );
      case "Email gate":
        return <div className="gk-caption">Viewers verify an emailed code before watching.</div>;
      case "Domain only":
        return requireEmail ? (
          <div className="share__protection-controls">
            <Input
              type="text"
              placeholder="Allowed email domains (comma-separated, optional)"
              aria-label="Allowed email domains"
              value={domainsInput}
              onChange={(e) => setDomainsInput(e.target.value)}
              disabled={protectBusy}
              className="share__protection-field"
            />
            <Button variant="secondary" size="sm" onClick={() => void saveDomains()} disabled={protectBusy}>
              Save
            </Button>
          </div>
        ) : (
          <div className="gk-caption">Turn on the email gate to limit it to domains.</div>
        );
    }
  }

  const subtitle = detail
    ? [detail.title, detail.duration_ms != null ? formatDuration(detail.duration_ms) : null].filter(Boolean).join(" · ")
    : undefined;

  return (
    <Dialog
      title={published ? "Your link is ready" : "Share"}
      subtitle={subtitle}
      icon={published ? "check-circle" : undefined}
      onClose={onClose}
      footer={
        <>
          <span className="gk-caption share__foot-note">
            <Icon name={visibility === "public" ? "globe" : "lock-simple"} size={15} />
            {visibility ? FOOTNOTE[visibility] : "Loading…"}
          </span>
          <Button variant="secondary" onClick={onClose} className="share__done">
            Done
          </Button>
        </>
      }
    >
      {error ? (
        <Callout tone="danger" icon="warning-circle">
          {error}
        </Callout>
      ) : null}

      {linkUrl ? (
        <div className="share__section share__link">
          <div className="share__label">Share link</div>
          <LinkField url={displayUrl(linkUrl)} href={linkUrl} />
          {visibility === "public" ? (
            <Button variant="danger" size="sm" onClick={revokeLink} disabled={busy} className="share__revoke">
              Revoke public link
            </Button>
          ) : null}
        </div>
      ) : visibility === "public" ? (
        <div className="share__section">
          <div className="share__label">Share link</div>
          <Button variant="secondary" icon="link-simple" onClick={createLink} disabled={busy}>
            Create public link
          </Button>
        </div>
      ) : null}

      <WhoCanWatchSection settings={settings} />

      {/* F3: the protections only apply to a live public link, so the section appears with it. */}
      {protections ? (
        <div className="share__section">
          <div className="share__label">Link protection</div>
          {protectError ? (
            <Callout tone="danger" icon="warning-circle">
              {protectError}
            </Callout>
          ) : null}
          <div className="share__protections">
            {PROTECTIONS.map((p) => (
              <div key={p.label} className="share__protection">
                <Icon name={p.icon} />
                <div className="share__protection-body">
                  <div className="share__protection-label">{p.label}</div>
                  {protectionControls(p.label)}
                </div>
                {p.label === "Email gate" ? (
                  <Switch
                    checked={requireEmail}
                    disabled={protectBusy}
                    onChange={(v) => void toggleRequireEmail(v)}
                    label="Require email verification"
                  />
                ) : null}
              </div>
            ))}
            {/* F13a/FR-ST25: the per-recording egress cap, for org admins; public delivery only. */}
            <BandwidthLimitRow grackleId={grackleId} />
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}
